import React, { useEffect, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { WarningTriangleColor } from "@/skylticons"
import type Sign from "@/signomatic/app/sign"
import type SignText from "@/signomatic/app/objects/sign_text"
import { debounce } from "lodash-es"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import { useMinimaticLoaded } from "@/signomatic/app/minimatic"
import { metaNameSign } from "@/common/util"

interface MinimaticInputsProps {
  minimatic?: Sign
  loadingFailed?: boolean
  text1?: string
  text2?: string
  text3?: string
  defaultText1?: string | null
  defaultText2?: string | null
  defaultText3?: string | null
  signType: string
  setText1: (text: string) => void
  setText2: (text: string) => void
  setText3: (text: string) => void
}

export const MinimaticInputs: React.FC<MinimaticInputsProps> = ({
  minimatic,
  loadingFailed,
  text1,
  text2,
  text3,
  defaultText1,
  defaultText2,
  defaultText3,
  signType,
  setText1,
  setText2,
  setText3,
}) => {
  const { t } = useTranslation()
  const [text1Object, setText1Object] = useState<SignText>()
  const [text2Object, setText2Object] = useState<SignText>()
  const [text3Object, setText3Object] = useState<SignText>()
  const [text1Size, setText1Size] = useState(Number)
  const [text2Size, setText2Size] = useState(Number)
  const [text3Size, setText3Size] = useState(Number)
  const [text1Error, setText1Error] = useState(false)
  const [text2Error, setText2Error] = useState(false)
  const [text3Error, setText3Error] = useState(false)
  const loaded = useMinimaticLoaded(minimatic)

  const triggerOnLoad = useCallback(async () => {
    if (!minimatic) {
      return
    }

    const objs = minimatic.allObjects.filter(obj => typeof obj.state.text === "string") as SignText[]

    if (objs[0] && !text1Size) {
      setText1Object(objs[0])
      setText1Size(objs[0].state.size)
    }

    if (objs[1] && !text2Size) {
      setText2Object(objs[1])
      setText2Size(objs[1].state.size)
    }

    if (objs[2] && !text3Size) {
      setText3Object(objs[2])
      setText3Size(objs[2].state.size)
    }

    if (!minimatic.activeObject) {
      if (text1 && text1Object && text1 !== defaultText1) {
        minimatic.setObjectActive(text1Object.state)
        minimatic.minimaticText(text1, text1Size, text2Size && text2Size, text3Size && text3Size)
        minimatic.setObjectActive(null)
      }

      if (text2 && text2Object && text2 !== defaultText2) {
        minimatic.setObjectActive(text2Object.state)
        minimatic.minimaticText(text2, text2Size)
        minimatic.setObjectActive(null)
      }

      if (text3 && text3Object && text3 !== defaultText3) {
        minimatic.setObjectActive(text3Object.state)
        minimatic.minimaticText(text3, text3Size)
        minimatic.setObjectActive(null)
      }
    }
  }, [
    minimatic,
    text1,
    text2,
    text3,
    defaultText1,
    defaultText2,
    defaultText3,
    text1Object,
    text2Object,
    text3Object,
    text1Size,
    text2Size,
    text3Size,
  ])

  useEffect(() => {
    if (loaded) {
      triggerOnLoad()
    }
  }, [loaded, triggerOnLoad])

  const roomError = (minimatic: Sign, textObject: SignText) => {
    const alignmentPadding =
      textObject.state.justify === "center"
        ? 0
        : textObject.state.justify === "right"
          ? minimatic.state.widthPx / 2 - textObject.state.left
          : minimatic.state.widthPx / 2 + textObject.state.left
    const availableSize = (minimatic.state.widthPx / 2 - Math.abs(textObject.state.left)) * 2
    const roomForText = textObject.state.justify === "center" ? availableSize : minimatic.state.widthPx

    return (
      (textObject.width || 0) + alignmentPadding + minimatic.minimaticExtraPadding(textObject) > roomForText &&
      textObject.state.size === minimatic.minTextSize
    )
  }

  const handleTextError = useCallback(() => {
    if (!minimatic) {
      return
    }

    if (text1Object && text1Object.height) setText1Error(roomError(minimatic, text1Object))

    if (text2Object && text2Object.height) setText2Error(roomError(minimatic, text2Object))

    if (text3Object && text3Object.height) setText3Error(roomError(minimatic, text3Object))
  }, [minimatic, text1Object, text2Object, text3Object])

  const handleInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, textNr: number) => {
      if (textNr === 1) {
        setText1(e.target.value)
      } else if (textNr === 2) {
        setText2(e.target.value)
      } else if (textNr === 3) {
        setText3(e.target.value)
      }

      if ((!text1Object && textNr === 1) || (!text2Object && textNr === 2) || (!text3Object && textNr === 3)) {
        return
      }

      if (minimatic) {
        if (!minimatic.activeObject) {
          if (textNr === 1 && text1Object) {
            minimatic.setObjectActive(text1Object.state)
          } else if (textNr === 2 && text2Object) {
            minimatic.setObjectActive(text2Object.state)
          } else if (textNr === 3 && text3Object) {
            minimatic.setObjectActive(text3Object.state)
          }
        }

        if (textNr === 1) {
          minimatic.minimaticText(e.target.value, text1Size, text2Size && text2Size, text3Size && text3Size)
        } else if (textNr == 2) {
          minimatic.minimaticText(e.target.value, text2Size)
        } else if (textNr == 3) {
          minimatic.minimaticText(e.target.value, text3Size)
        }

        handleTextError()
      }
    },
    [
      handleTextError,
      setText1,
      setText2,
      setText3,
      minimatic,
      text1Object,
      text1Size,
      text2Object,
      text2Size,
      text3Object,
      text3Size,
    ]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputChange = useCallback(debounce(handleInputChange, 150, { leading: false, trailing: true }), [
    handleInputChange,
  ])

  const handleInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>, textNr: number) => {
      setTimeout(() => e.target.setSelectionRange(0, e.target.value.length), 100)

      if (textNr === 1) {
        setText1(e.target.value)
      } else if (textNr == 2) {
        setText2(e.target.value)
      } else if (textNr == 3) {
        setText3(e.target.value)
      }

      if (minimatic) {
        if (text1Object && textNr === 1) {
          minimatic.setObjectActive(text1Object.state)
        }

        if (text2Object && textNr === 2) {
          minimatic.setObjectActive(text2Object.state)
        }

        if (text3Object && textNr === 3) {
          minimatic.setObjectActive(text3Object.state)
        }
      }
    },
    [minimatic, text1Object, text2Object, text3Object, setText1, setText2, setText3]
  )

  const ssrDisabled = useSSRDisabled()
  const disabled = (ssrDisabled || !minimatic) && !loadingFailed

  const errorTooltip = (
    <div className="mt-1 flex items-center rounded border-1 border-warning bg-warning-bg p-3">
      <WarningTriangleColor className="h-6 w-6 min-w-[24px] self-baseline [&_.primary]:fill-warning [&_.primary]:text-warning [&_.secondary]:fill-ink-100 [&_.secondary]:text-ink-100" />
      <span className="ml-2 text-base">{t("examples.text_too_long")}</span>
    </div>
  )

  return (
    <div>
      <div className="mb-3">
        <p className="label paragraph-xs bold">
          {metaNameSign(signType) ? t("address.name") : `${t("toolbox.text")}${defaultText2 ? " 1" : ""}`}
        </p>
        <div className="input-wrapper">
          <input
            id="text1"
            className="form-control"
            type="text"
            autoComplete="off"
            defaultValue={defaultText1 || ""}
            disabled={disabled}
            onChange={e => debouncedInputChange(e, 1)}
            onFocus={e => handleInputFocus(e, 1)}
            onBlur={() => minimatic && minimatic.setObjectActive(null)}
          />
        </div>
        {text1Error && errorTooltip}
      </div>

      {defaultText2 && (
        <div className="mb-3">
          <p className="label paragraph-xs bold">
            {metaNameSign(signType) ? t("examples.orgtitle") : `${t("toolbox.text")} 2`}
          </p>
          <div className="input-wrapper">
            <input
              id="text2"
              className="form-control"
              type="text"
              autoComplete="off"
              defaultValue={defaultText2}
              disabled={disabled}
              onChange={e => debouncedInputChange(e, 2)}
              onFocus={e => handleInputFocus(e, 2)}
              onBlur={() => minimatic && minimatic.setObjectActive(null)}
            />
          </div>
          {text2Error && errorTooltip}
        </div>
      )}

      {defaultText3 && (
        <div className="mb-3">
          <p className="label paragraph-xs bold">{`${t("toolbox.text")} 3`}</p>
          <div className="input-wrapper">
            <input
              id="text3"
              className="form-control"
              type="text"
              autoComplete="off"
              defaultValue={defaultText3}
              disabled={disabled}
              onChange={e => debouncedInputChange(e, 3)}
              onFocus={e => handleInputFocus(e, 3)}
              onBlur={() => minimatic && minimatic.setObjectActive(null)}
            />
          </div>
          {text3Error && errorTooltip}
        </div>
      )}
    </div>
  )
}
